import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { BottomSheetPopup } from './BottomSheetPopup';
import { Clickable } from './Clickable';
import { appTheme, useThemeColors } from '../../shared/theme';
import { registerActionSheetHost, type ActionSheetConfig } from '../ui/showActionSheet';

/**
 * Android action-sheet parity host — mount once under v3 `AppNavigator`.
 * iOS uses native `ActionSheetIOS` via `showActionSheet`.
 */
export function ActionSheetHost() {
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [config, set_config] = useState<ActionSheetConfig | null>(null);

  useEffect(() => {
    registerActionSheetHost({
      show: next => set_config(next),
    });
    return () => registerActionSheetHost(null);
  }, []);

  const close = useCallback(() => set_config(null), []);

  const pick_option = useCallback(
    (on_select: () => void) => {
      close();
      on_select();
    },
    [close],
  );

  return (
    <BottomSheetPopup
      visible={config != null}
      onClose={close}
      title={config?.title}
      showDragHandle
      animationType="slide"
      sheetHeightMode="fitContent">
      <View style={styles.body}>
        {config?.message ? (
          <Text style={styles.message} accessibilityRole="text">
            {config.message}
          </Text>
        ) : null}
        {config?.options.map((option, index) => (
          <Clickable
            key={`${option.label}-${index}`}
            onPress={() => pick_option(option.onSelect)}
            style={styles.option_row}
            contentStyle={styles.option_row_inner}
            accessibilityRole="button"
            accessibilityLabel={option.label}>
            <Text style={[styles.option_label, option.destructive && styles.option_destructive]}>{option.label}</Text>
          </Clickable>
        ))}
        <Clickable
          onPress={close}
          style={styles.cancel_row}
          contentStyle={styles.cancel_row_inner}
          accessibilityRole="button"
          accessibilityLabel={config?.cancelLabel ?? ''}>
          <Text style={styles.cancel_label}>{config?.cancelLabel}</Text>
        </Clickable>
      </View>
    </BottomSheetPopup>
  );
}

function createStyles(colors: typeof appTheme.colors) {
  return StyleSheet.create({
    body: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.lg,
      gap: appTheme.spacing.sm,
    },
    message: {
      ...appTheme.typography.bodySmall,
      color: colors.textSecondary,
      textAlign: 'center',
      marginBottom: appTheme.spacing.xs,
    },
    option_row: {
      borderRadius: appTheme.borderRadius.lg,
      backgroundColor: colors.surfaceLight,
      overflow: 'hidden',
    },
    option_row_inner: {
      minHeight: 52,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.md,
    },
    option_label: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '600',
      color: colors.text,
      textAlign: 'center',
    },
    option_destructive: {
      color: colors.error,
    },
    cancel_row: {
      marginTop: appTheme.spacing.xs,
      borderRadius: appTheme.borderRadius.lg,
      backgroundColor: colors.backgroundFade,
      overflow: 'hidden',
    },
    cancel_row_inner: {
      minHeight: 52,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: appTheme.spacing.md,
    },
    cancel_label: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '700',
      color: colors.textSecondary,
      textAlign: 'center',
    },
  });
}
